// Lion Auto's low-outlier rule as the page words it. The server (backend
// compMatchRules applyOutliers) drops the cheapest comps a group's rule
// (criteria lowerOutlierMethod / lowerOutlierAmount / lowerOutlierThreshold)
// calls outliers, so the anchor comp is the cheapest comp left: with N
// dropped it is the (N + 1)th cheapest. New groups start with no rule (a
// pricer picks one per group, e.g. Drive's threshold of position, which the
// editor starts at 25% under the 3rd cheapest); saved groups keep theirs.
// These helpers name
// the rule, how many comps it dropped and what that made of the anchor comp,
// for the editor, the legend, the ladder bar, the table head, the group card,
// the match banners and the Auto run summary. Display only: nothing here
// picks a comp or computes a price. Pure; tested in lionAutoOutliers.test.js.
import { pctText } from './lionAutoLadder';

// The editor's name for the rule (its fieldset under Advanced filters)
const FILTER_NAME = 'Exclude lowest listings';
// The rules that measure every comp against the Nth cheapest (Drive's
// threshold of position)
const POSITION_METHODS = Object.freeze(['percentThresholdOfPosition', 'currencyThresholdOfPosition']);

/** The anchor comp's "2nd cheapest · 1 low outlier dropped" note, as its tooltip */
export const ANCHOR_OUTLIER_TITLE = `The group's low-outlier rule (${FILTER_NAME}, under Advanced filters) dropped the cheapest comps, `
  + 'so the anchor comp is the cheapest one left. The table lists the dropped ones as Low outlier.';

const usd = (v) => `$${v.toFixed(2)}`;

// A count as sent: a whole number >= 0 (a numeric string reads as one), else null
function countOf(v) {
  if (typeof v === 'string' ? v.trim() === '' : typeof v !== 'number') return null;
  const n = Number(v);
  return Number.isInteger(n) && n >= 0 ? n : null;
}

// An amount or threshold as the server keeps it: a finite number >= 0, else 0
function amountOf(v) {
  if (typeof v === 'string' ? v.trim() === '' : typeof v !== 'number') return 0;
  const n = Number(v);
  return Number.isFinite(n) && n >= 0 ? n : 0;
}

// The position a position rule measures from, as the server reads it: whole, at least 1
const positionOf = (amount) => Math.max(1, Math.floor(amountOf(amount)));

// How far under that comp a comp must be to be ignored: "25%" or "$20.00"
const thresholdText = (method, threshold) => (method === 'percentThresholdOfPosition'
  ? pctText(amountOf(threshold))
  : usd(amountOf(threshold)));

/** "1st", "2nd", "3rd", "4th", "11th", "21st"; null unless a whole number above 0 */
export function ordinal(n) {
  const k = countOf(n);
  if (!k) return null;
  const teen = k % 100 >= 11 && k % 100 <= 13;
  const suffix = teen ? 'th' : ({ 1: 'st', 2: 'nd', 3: 'rd' }[k % 10] || 'th');
  return `${k}${suffix}`;
}

/**
 * How many comps a match result's low-outlier rule dropped. With an anchor
 * comp, the ones under it: resolved.outlier.underAnchor (its own pass: the
 * sections', or the zone's for a zone anchor, whose sections' dropped comps
 * can be dearer than it). With none (or an older server's result without
 * underAnchor), every pass: resolved.outlier.dropped, else
 * excludedCounts.OUTLIER. Each only when a whole number >= 0; else 0.
 */
export function outliersDroppedOf(result) {
  if (result?.anchorComp) {
    const underAnchor = countOf(result?.resolved?.outlier?.underAnchor);
    if (underAnchor !== null) return underAnchor;
  }
  const fromResolved = countOf(result?.resolved?.outlier?.dropped);
  if (fromResolved !== null) return fromResolved;
  return countOf(result?.excludedCounts?.OUTLIER) ?? 0;
}

/** "1 low outlier dropped" / "3 low outliers dropped"; null for none or junk */
export function outliersDroppedText(n) {
  const k = countOf(n);
  if (!k) return null;
  return `${k} low outlier${k === 1 ? '' : 's'} dropped`;
}

/**
 * The anchor comp's note when the rule dropped comps under it: "2nd cheapest
 * · 1 low outlier dropped" (the (n + 1)th cheapest). A zone anchor (scope
 * 'zone') gets the count only: it ranks among the zone's comps, not the
 * group's sections', so an ordinal would mislead. null for none.
 * @param {number} n        comps dropped (anchorComp.outliersDropped, outliersDroppedOf)
 * @param {string} [scope]  the anchor comp's scope: 'section' (or none sent) | 'zone'
 */
export function anchorOutlierText(n, scope) {
  const dropped = outliersDroppedText(n);
  if (!dropped) return null;
  return scope === 'zone' ? dropped : `${ordinal(countOf(n) + 1)} cheapest · ${dropped}`;
}

/**
 * The rule in words, read the way the server applies it: "Ignore comps more
 * than 25% under the 3rd cheapest" ("$20.00" for the $ rule; "under the
 * cheapest" for position 1, which drops nothing), "Drop the 2 cheapest
 * comps", "Drop comps under $50.00 L", "Drop the cheapest 10% of comps".
 * null when off ('none') or no method; a method this page doesn't know is
 * named as sent.
 * @param {string} method       lowerOutlierMethod (or resolved.outlier.method)
 * @param {number} [amount]     lowerOutlierAmount: position, listings, $ or %
 * @param {number} [threshold]  lowerOutlierThreshold: how far under the position's comp, % or $
 * @returns {string|null}
 */
export function outlierRuleText(method, amount, threshold) {
  if (typeof method !== 'string' || !method || method === 'none') return null;
  if (POSITION_METHODS.includes(method)) {
    const p = positionOf(amount);
    return `Ignore comps more than ${thresholdText(method, threshold)} under the ${p === 1 ? 'cheapest' : `${ordinal(p)} cheapest`}`;
  }
  if (method === 'quantity') {
    const k = Math.floor(amountOf(amount));
    return k === 1 ? 'Drop the cheapest comp' : `Drop the ${k} cheapest comps`;
  }
  if (method === 'currency') return `Drop comps under ${usd(amountOf(amount))} L`;
  if (method === 'percentage') return `Drop the cheapest ${pctText(amountOf(amount))} of comps`;
  return method;
}

// Position 1 measures from the cheapest comp itself: nothing is under it
const POSITION_ONE_HINT = 'Position 1 is the cheapest comp and no comp is priced under it, so nothing is ignored: '
  + 'set 2 or more to keep a lone cheap comp from anchoring the ladder.';

/**
 * What a position rule does, under the editor's method select: "A lone
 * cheap comp can't anchor the ladder: any comp priced more than 25% under
 * the 3rd cheapest comp is ignored. With fewer than 3 comps nothing is
 * ignored." Position 1 says it ignores nothing. null for every other method.
 * @returns {string|null}
 */
export function positionRuleHint(method, amount, threshold) {
  if (!POSITION_METHODS.includes(method)) return null;
  const p = positionOf(amount);
  if (p === 1) return POSITION_ONE_HINT;
  return `A lone cheap comp can't anchor the ladder: any comp priced more than ${thresholdText(method, threshold)} `
    + `under the ${ordinal(p)} cheapest comp is ignored. With fewer than ${p} comps nothing is ignored.`;
}

/** The ALL_COMPS_OUTLIERS banner, with the count when known (outliersDroppedOf) */
export function allOutliersText(n) {
  const k = countOf(n);
  const loosen = `loosen or turn off ${FILTER_NAME} under Advanced filters.`;
  if (k === 1) return `The only matching comp was dropped as a low outlier — ${loosen}`;
  if (k) return `Every one of the ${k} matching comps was dropped as a low outlier — ${loosen}`;
  return `Every matching comp was dropped as a low outlier — ${loosen}`;
}
